import { pickBy } from 'lodash-es';
import { handleQueryError } from '@/core/shared/helpers/ErrorHelper';
import { tryRequest } from '@/core/shared/helpers/RetryHelper';
import { gqlWithoutMiddleware, gql } from '@/setup/api';

// import { difficultyToInt, objListToArr } from '@/launcher/helpers'

const gameFragment = `
  id
  appId
  title
  description
  cds
  imageUrl
  videoUrl
  featured
  difficulty
  tags {
    text
    order
    internal
  }
  singleplayer
  multiplayer
  controlsTrigger
  controlsGrip
  controlsMenu
  controlsTouchpad
  controlsExtra
  launchType
  launchMethod
  manifestKey
  appId
  path
  exe
  arguments
  uri
`;
const stationQuery = `
  id
  title
  volume
  playingType
  playing {
    ${gameFragment}
    controlsTrigger
    controlsGrip
    controlsMenu
    controlsTouchpad
    controlsExtra
  }
  organization {
    id
    name
    state
    country
    isActive
    advertisingEnabled
    # For Permissions
    subscriptionExclusion
    contentOnly
    type
  }
  experience {
    id
    banners {
      id
      type
      position
      size
      side
      imageUrl
    }
    tutorial {
      id
    }
    launcher {
      id
      title
    }
    gameReviewsEnabled
    type
  }
  calling
  locale
  #locale { //TODO https://springboardvr.atlassian.net/browse/DEV-736
  #  key
  #  text
  #}
  location {
    id
    title
    state
    country
  }
  # stationDevices {
  #}
  #logoUrl
  steamPath
  themeParkModeEnabled
  launcherEnabled
  timerEnabled
  defaultDashboardElementsEnabled
  overlayRestrictionsEnabled
  quitGameOnTimeUp
  maintenanceModeEnabled
  idleMode
  idleModeVolume
  ageLimit
  version
  launcherContentPreviewEnabled
  safetyMode
  kioskQrCode
`;
export const getStationVideosQuery = stationId => {
  const variables = {
    stationId,
  };
  const query = `
  query($stationId: ID) {
    stationVideos(stationId: $stationId)
  }
  `;
  return gql
    .request(query, variables, { cancelKey: 'getStationVideos' })
    .catch(handleQueryError('getStationVideos'));
};

export const getGameQuery = id => {
  const query = `
  query($id: ID) {
    game(id: $id) {
      ${gameFragment}
    }
  }
  `;
  return gql
    .request(query, { id }, { cancelKey: 'getGame' })
    .catch(handleQueryError('getGame'));
};
export const getTutorialQuery = id => {
  const query = `
  query($tutorial: TutorialInput) {
    tutorial(tutorial: $tutorial) {
      id
      title
      type
    }
  }
  `;
  return gql
    .request(query, { tutorial: { id } }, { cancelKey: 'getTutorial' })
    .catch(handleQueryError('getTutorial'));
};

export const getStationQuery = stationId => {
  const query = `
  query($stationId: ID) {
    station(stationId: $stationId) {
      ${stationQuery}
    }
  }
  `;
  return gql
    .request(query, { stationId }, { cancelKey: 'getStation' })
    .catch(handleQueryError('getStation'));
};

export const getStationTagsQuery = stationId => {
  const query = `
    query($stationId: ID) {
      stationTags(stationId: $stationId) {
        id
        text
        internal
        games {
          ${gameFragment}
        }
      }
      stationLauncherTags(stationId: $stationId) {
        id
        text
        internal
        games {
          ${gameFragment}
        }
      }
    }
  `;
  return tryRequest((retry, { remaining }) => {
    return gql
      .request(query, { stationId }, { cancelKey: 'getStationTags' })
      .catch(e => {
        if (remaining) return retry();
        else throw e;
      });
  });
};

export const getStationGamesQuery = ({
  stationId,
  difficulties,
  tags,
  search,
  singleplayer,
  multiplayer,
}) => {
  const variables = {
    stationId,
    difficulties,
    tags,
    search,
    singleplayer,
    multiplayer,
  };
  const query = `
    query(
      $stationId: ID,
      $difficulties: [Int],
      $tags: [ID],
      $search: String,
      $singleplayer: Boolean,
      $multiplayer: Boolean
    ) {
      stationGames(
        stationId: $stationId,
        difficulties: $difficulties,
        tags: $tags,
        search: $search,
        singleplayer: $singleplayer,
        multiplayer: $multiplayer,
      ) {
        ${gameFragment}
      }
    }
  `;

  return gql
    .request(query, variables, { cancelKey: 'filteredGames' })
    .catch(handleQueryError('filteredGames'));
};

export const getLocalesQuery = () => {
  const query = `
    query {
      locales {
        key
        text
      }
    }
  `;
  return gql.request(query).catch(handleQueryError('getLocalesQuery'));
};

export const storeGameSessionsMutation = payload => {
  // Here we have to batch 2 mutations in the same roundtrip this is why we can't use variables support :(
  const mutationFactory = (name, vars) => {
    const stringifyVars = vars => {
      return Object.keys(vars).reduce((res, key) => {
        const string =
          typeof vars[key] === 'object' && vars[key]
            ? `{ ${stringifyVars(vars[key])}
        }`
            : JSON.stringify(vars[key]) || null;
        return `${res}
          ${key}: ${string},`;
      }, '');
    };
    const stringifiedVars = stringifyVars(vars);
    return `
      ${name}Session: storeGameSession(
        gameSession: {
          ${stringifiedVars}
        }
      ) {
        id
      }
    `;
  };
  const sessionsMutations =
    payload instanceof Array
      ? payload.map((vars, i) => mutationFactory(!i ? 'prev' : 'new', vars))
      : mutationFactory('new', payload);
  const mutation = `
    mutation {
      ${sessionsMutations}
    }
  `;
  return gqlWithoutMiddleware
    .request(mutation, null, { cancelKey: 'storeGameSession', timeout: 30000 })
    .catch(handleQueryError('sessionsMutations'));
};

export const updateStationMutation = (
  station,
  { cancelKey = 'updateStation', withoutMiddleware },
) => {
  const formattedStation = pickBy(station, (v, k) => k.slice(0, 1) !== '_');
  if (!window.sbvrenv.CEFSHARP) {
    delete formattedStation.version;
  }
  const variables = {
    station: {
      ...formattedStation,
      powerStatus: true, // liveness probe backend
    },
  };
  const mutation = `
    mutation(
      $station: StationInput,
    ) {
      station: storeStation(station:$station) {
        ${stationQuery}
      }
    }
  `;
  gql.cancelLast('getStation');
  const gqlService = withoutMiddleware ? gqlWithoutMiddleware : gql;
  return gqlService
    .request(mutation, variables, { cancelKey })
    .catch(handleQueryError('updateStationMutation'));
};

export const updateDevicesMutation = (stationId, devices) => {
  const variables = {
    station: {
      id: stationId,
    },
    stationDevices: devices,
  };
  const mutation = `
    mutation(
      $station: StationInput,
      $stationDevices: [StationDeviceInput],
    ) {
      storeStationDevices(station:$station, stationDevices: $stationDevices) {
        id
      }
    }
  `;
  return gql
    .request(mutation, variables, { cancelKey: 'devicesMutation' })
    .catch(handleQueryError('devicesMutation'));
};

export const ratingMutation = ratingInput => {
  const variables = { rating: ratingInput };
  const mutation = `
    mutation(
      $rating: RatingInput,
    ) {
      storeRating(rating: $rating) {
        id
      }
    }
  `;
  return gql
    .request(mutation, variables)
    .catch(handleQueryError('ratingMutation'));
};

export const getCurrentTimeQuery = () => {
  return fetch('https://current-time.springboardvr.com')
    .then(response => {
      if (!response.ok) {
        throw response;
      }
      return response.json();
    })
    .catch(handleQueryError('getCurrentTime'));
};

export const getBookingsQuery = ({ locationId, stationId }) => {
  const variables = { locationId, stationId };
  const query = `
  query($locationId: ID, $stationId: ID) {
    bookingStationTimes(location: { id: $locationId }, station: { id: $stationId }) {
      id
      startedAt
      endTime
      pausedAt
      pausedDuration
      station {
        id
      }
      booking {
        id
        host {
          id
        }
      }
    }
  }
  `;
  return gql
    .request(query, variables, { cancelKey: 'getBookingsQuery' })
    .catch(handleQueryError('getBookings'));
};

export const getThemeParkGameQuery = stationId => {
  const variables = { station: { id: stationId } };
  const query = `
  query($station: StationInput) {
    stationThemeParkGame(station: $station) {
      id
    }
  }
  `;
  return gql
    .request(query, variables, { cancelKey: 'getThemeParkGameQuery' })
    .catch(handleQueryError('getThemeParkGameQuery'));
};

export const createCoinOpBookingMutation = stationId => {
  const variables = { station: { id: stationId } };
  const mutation = `
  mutation($station: StationInput) {
    createCoinOpBooking(station: $station) {
      id
    }
  }
  `;
  return gql
    .request(mutation, variables, { cancelKey: 'createCoinOpBookingMutation' })
    .catch(handleQueryError('createCoinOpBookingMutation'));
};
